// Persistent app-level banner shown beneath the top nav (and beneath the
// outbound `TravelRuleStatusBanner`) whenever the user has one or more
// inbound Notabene transfers awaiting customer-supplied sender info
// (status ACTION_REQUIRED / TR_INFO_REQUESTED / REQUIRES_PII).
//
// Click → routes to `/transfers/:id`, which opens the unified detail dialog
// containing the `DepositActionRequiredForm`. Hidden when the user is already
// on a `/transfers/:id` route to avoid duplicating the dialog's prompt.

import React from "react";
import { Box, Typography } from "@mui/material";
import { WarningAmberOutlined } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";

import { useGetNotabeneTransfersQuery } from "src/store/features/api/notabene";
import { useTrEnabled } from "src/utils/hooks/useTrEnabled";
import { useUserSlice } from "src/store/features/user.slice";

const ACTION_REQUIRED_STATUSES = new Set([
  "TR_INFO_REQUESTED",
  "ACTION_REQUIRED",
  "REQUIRES_PII",
]);

const isActionRequiredInbound = (t: {
  direction?: string;
  status?: string;
}): boolean => {
  if (t.direction !== "INCOMING") return false;
  if (!t.status) return false;
  return ACTION_REQUIRED_STATUSES.has(t.status.toUpperCase());
};

export const DepositActionRequiredBanner = (): JSX.Element | null => {
  const history = useHistory();
  const location = useLocation();
  // Master TR kill-switch — when inbound TR is disabled globally, hide the
  // banner even if stale ACTION_REQUIRED rows still exist in the cache.
  // Stops the customer being prompted to "provide info" for a flow that
  // the backend has been told to skip.
  const trOn = useTrEnabled("INBOUND");
  const {
    details: { customerId },
  } = useUserSlice();
  // RTK Query dedupes — if `useTravelRuleHydrate` already triggered the
  // fetch on mount we read from cache; if it skipped (redux already had an
  // outbound txId), this is the first caller and triggers the fetch itself.
  // The transfers cache is keyed per-profile (same customerId arg the
  // hydrate hook uses), and `currentData` keeps a stale previous-profile
  // list from flashing inbound rows the active profile can't act on.
  const { currentData: transfers } = useGetNotabeneTransfersQuery(customerId, {
    skip: !customerId,
  });

  if (!trOn) return null;
  if (location.pathname.startsWith("/transfers/")) return null;

  const matches = (transfers ?? []).filter(isActionRequiredInbound);
  if (matches.length === 0) return null;

  // Most-recent first — the list endpoint already orders by createdAt desc.
  // For multi-match, route to the first; the banner re-renders with count-1
  // after the customer submits, walking them through the queue.
  const target = matches[0];
  const count = matches.length;

  const title =
    count === 1
      ? "A deposit needs your input to clear compliance"
      : `${count} deposits need your input to clear compliance`;
  const body =
    "We're missing sender information. Funds are held until you confirm — this usually takes a minute.";

  const handleView = () => {
    history.push(`/transfers/${target.txId}`);
  };

  // Warning-amber palette so the banner reads as customer-actionable and
  // visually distinct from the gray/green outbound `TravelRuleStatusBanner`
  // when both are stacked.
  const palette = {
    bg: "#FFF8E1",
    border: "#FCD34D",
    title: "#7A4F01",
    body: "#92660B",
    btn: "#B45309",
  };

  return (
    <Box
      role="status"
      data-testid="deposit-action-required-banner"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 1.25,
        px: 3,
        backgroundColor: palette.bg,
        borderBottom: `1px solid ${palette.border}`,
        // Match the outbound banner's left offset so neither sits underneath
        // the persistent side drawer (220px wide). Mobile drawer overlays
        // content so no offset is needed there.
        ml: { xs: 0, md: "220px" },
      }}>
      <Box display="flex" alignItems="center" gap={1.5}>
        <WarningAmberOutlined sx={{ fontSize: 22, color: palette.btn }} />
        <Box>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 600, color: palette.title }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: palette.body }}>
            {body}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          component="button"
          onClick={handleView}
          data-testid="deposit-action-required-banner-cta"
          sx={{
            backgroundColor: palette.btn,
            color: "#FFFFFF",
            border: "none",
            borderRadius: "999px",
            padding: "6px 14px",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}>
          {count === 1 ? "Provide info" : "Review deposits"}
        </Box>
      </Box>
    </Box>
  );
};
